// Orchestrator for the BMD student-side test-taking flow. Composes the
// reusable pieces (useBmdSession, useBmdTimer, <DQDisplay>, <FollowUpQuestions>,
// <BmdTimer>) and drives the per-question loop:
//
//   1. useBmdSession resolves the current_question from the server.
//   2. <BmdTimer> counts down MAIN, then BUFFER, then EXPIRED. Both come
//      from bmd.constants (60s and 10s); neither is hard-coded here.
//   3. The student picks an answer in <DQDisplay>, which pauses the timer and
//      surfaces <FollowUpQuestions>. Every year is asked how confident they
//      were; only Year 4 and above are asked to write why. Both are optional.
//   4. Submit calls submitBmdAnswer, refetches the session, resets the timer,
//      and moves to the next DQ.
//   5. A timer that fully expires with no answer auto-skips as TIMEOUT and
//      runs the same flow.
//   6. "Skip for now" calls skipBmdQuestion with MANUAL and runs the same flow.
//
// This is the only place that calls submit or skip, which keeps the leaf
// components decoupled from the session lifecycle.

import React from "react"
import { Modal, ModalBody, ModalFooter } from "reactstrap"
import { useBmdSession } from "../../hooks/diagnostics/useBmdSession"
import { useBmdTimer } from "../../hooks/diagnostics/useBmdTimer"
import {
  BMD_FOLLOW_UP_TIMEOUT_ACTION,
  BMD_FOLLOW_UP_TIMER_SECONDS,
  BMD_FOLLOW_UPS_SHARE_QUESTION_TIMER,
  BMD_LOCK_ANSWER_AFTER_SELECT,
} from "../../constants/diagnostics/bmd.constants"
import { DQDisplay } from "./DQDisplay"
import { BmdTimer } from "./BmdTimer"
import { BmdQuestionTracker } from "./BmdQuestionTracker"
import { BmdFinishCard } from "./BmdFinishCard"
import { BMD_UI } from "../../constants/diagnostics/bmd.theme"
import {
  FollowUpQuestions,
  type FollowUpQuestionsValue,
} from "./FollowUpQuestions"
import {
  submitBmdAnswer,
  skipBmdQuestion,
  completeBmdTopic,
} from "../../modules/diagnostics/services/bmd.service"
import type { SubmitBmdAnswerResponse } from "../../modules/diagnostics/types/bmd.types"
import { BMD_COPY } from "../../constants/diagnostics/bmd.constants"
import { plainText } from "../../modules/diagnostics/utils/text"

export type BmdTestPlayerProps = {
  diagnosticsSessionKey: string
  onSessionComplete?: () => void
  /**
   * Where "Back to my activities" goes on the finish card. Omitted, the button
   * is not drawn rather than drawn dead.
   */
  onBack?: () => void
  className?: string
}

const emptyFollowUp: FollowUpQuestionsValue = { reason: "", confident: null }

export const BmdTestPlayer: React.FC<BmdTestPlayerProps> = ({
  diagnosticsSessionKey,
  onSessionComplete,
  onBack,
  className,
}) => {
  const { session, loading, error, refetch } =
    useBmdSession(diagnosticsSessionKey)

  const currentQuestion = session?.current_question || null
  const currentQuestionKey = currentQuestion?.pk_session_question_key || null

  // Per-question local state. Resets whenever the server hands us a new
  // session_question_key.
  const [selectedAnswerId, setSelectedAnswerId] = React.useState<number | null>(
    null,
  )
  const [followUp, setFollowUp] =
    React.useState<FollowUpQuestionsValue>(emptyFollowUp)
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const [submitError, setSubmitError] = React.useState<string | null>(null)
  const [interventionMessage, setInterventionMessage] = React.useState<
    string | null
  >(null)
  // Non-blocking inline notice. The per-turn reset below deliberately leaves
  // it alone: it is raised as one question ends and has to survive into the
  // next screen to be read at all. It clears when the student next answers.
  const [notice, setNotice] = React.useState<string | null>(null)

  // Start time to compute duration_seconds. A ref (not state) so the timer
  // hook rerendering every second doesn't stamp a new time.
  const questionStartRef = React.useRef<number>(Date.now())
  // Ensure we only submit/skip once per question, even if timer + click race.
  const submittedForQuestionRef = React.useRef<string | null>(null)

  // Bumped after every completed turn, submit or skip. The question key is not
  // a reliable "something changed" signal: a skipped question is re-served
  // later with the same key, so keying the resets below off the key alone
  // meant they never ran. The double-submit guard stayed armed and the timer
  // stayed EXPIRED, freezing the student on the question until the component
  // unmounted. Identity is the turn, not the question.
  const [turnSeq, setTurnSeq] = React.useState(0)
  const turnId = `${currentQuestionKey ?? ""}#${turnSeq}`

  React.useEffect(() => {
    // A new turn started (new question, or the same one served again):
    // reset all per-question state.
    setSelectedAnswerId(null)
    setFollowUp(emptyFollowUp)
    setSubmitError(null)
    setInterventionMessage(null)
    questionStartRef.current = Date.now()
    submittedForQuestionRef.current = null
  }, [turnId])

  const computeDurationSeconds = (): number =>
    Math.max(0, Math.floor((Date.now() - questionStartRef.current) / 1000))

  // Deferred advance when the student dismisses the intervention modal.
  // Stores the work finishTurn would have done, so the refetch + topic
  // completion happen AFTER the student has read the intervention message.
  const [pendingAdvance, setPendingAdvance] = React.useState<null | {
    topicKeyAtSubmit: string | null
    shouldCompleteTopic: boolean
  }>(null)

  const advanceFromIntervention = React.useCallback(async () => {
    const pending = pendingAdvance
    setInterventionMessage(null)
    setPendingAdvance(null)
    if (!pending) return
    if (pending.shouldCompleteTopic && pending.topicKeyAtSubmit) {
      try {
        await completeBmdTopic({
          diagnostics_session_key: diagnosticsSessionKey,
          pk_session_topic_key: pending.topicKeyAtSubmit,
        })
      } catch (err) {
        setSubmitError(
          err instanceof Error ? err.message : BMD_COPY.ERROR_SUBMIT,
        )
      }
    }
    refetch()
    setTurnSeq((s) => s + 1)
  }, [pendingAdvance, diagnosticsSessionKey, refetch])

  // After submit/skip, if the server signals the topic is done, call
  // completeBmdTopic so the server can decide remediation vs next_topic vs
  // session_complete. Then refetch the session state to advance the player.
  // If intervention fired, pause until the student dismisses the modal.
  const finishTurn = React.useCallback(
    async (res: SubmitBmdAnswerResponse, topicKeyAtSubmit: string | null) => {
      const needsTopicComplete =
        res.next === "topic-complete" && !!topicKeyAtSubmit
      // A failed second attempt on a fundamental misconception is not a
      // blocking event. The student is told inline and carries straight on,
      // and the tutor picks it up from the report. Only the repeated-timeout
      // case stops the flow, because there the point is to interrupt a student
      // who has stalled twice.
      if (res.intervention_triggered) {
        if (res.intervention_reason === "FM_RETRY_FAILED") {
          setNotice(BMD_COPY.INTERVENTION_NOTICE)
          // fall through and advance normally
        } else {
          setInterventionMessage(BMD_COPY.INTERVENTION_FLAGGED)
          setPendingAdvance({
            topicKeyAtSubmit,
            shouldCompleteTopic: needsTopicComplete,
          })
          return
        }
      }
      if (needsTopicComplete && topicKeyAtSubmit) {
        try {
          await completeBmdTopic({
            diagnostics_session_key: diagnosticsSessionKey,
            pk_session_topic_key: topicKeyAtSubmit,
          })
        } catch (err) {
          setSubmitError(
            err instanceof Error ? err.message : BMD_COPY.ERROR_SUBMIT,
          )
        }
      }
      refetch()
      // Close the turn even if the server hands back the same question (a skip
      // re-serves it at the end of the topic). Without this the resets above
      // never fire and the player locks up.
      setTurnSeq((s) => s + 1)
    },
    [diagnosticsSessionKey, refetch],
  )

  // Capture the topic key at submit time so the transition uses the same
  // topic even if useBmdSession has already refetched by the time finishTurn
  // runs.
  const currentTopicKey = session?.current_topic_key || null

  // handleTimeout is defined before submitAnswer, and a callback cannot close
  // over a value declared after it. These refs carry the current answer and the
  // current submit across that boundary - the same pattern the follow-up
  // handlers already use, and the reason they use it.
  const selectedAnswerIdRef = React.useRef<number | null>(null)
  const submitAnswerRef = React.useRef<
    ((allowPartialFollowUp: boolean) => Promise<void>) | null
  >(null)

  const handleTimeout = React.useCallback(() => {
    if (!currentQuestion || !currentQuestionKey) return
    if (submittedForQuestionRef.current === currentQuestionKey) return
    // With one shared window, time can run out AFTER an answer is chosen. That
    // is not a skip - the student answered - so their answer is submitted with
    // whatever follow-ups they managed, exactly as the separate follow-up
    // countdown used to do. Skipping here would throw away a real answer.
    if (
      BMD_FOLLOW_UPS_SHARE_QUESTION_TIMER &&
      selectedAnswerIdRef.current != null
    ) {
      void submitAnswerRef.current?.(true)
      return
    }
    submittedForQuestionRef.current = currentQuestionKey
    const topicKeyAtSubmit = currentTopicKey
    void (async () => {
      try {
        setNotice(null)
        setSubmitting(true)
        const res = await skipBmdQuestion({
          diagnostics_session_key: diagnosticsSessionKey,
          pk_session_question_key: currentQuestion.pk_session_question_key,
          skip_reason: "TIMEOUT",
          duration_seconds: computeDurationSeconds(),
        })
        await finishTurn(res, topicKeyAtSubmit)
      } catch (err) {
        setSubmitError(
          err instanceof Error ? err.message : BMD_COPY.ERROR_SUBMIT,
        )
        submittedForQuestionRef.current = null
      } finally {
        setSubmitting(false)
      }
    })()
  }, [
    currentQuestion,
    currentQuestionKey,
    currentTopicKey,
    diagnosticsSessionKey,
    finishTurn,
  ])

  const timer = useBmdTimer({
    autoStart: true,
    onTimeout: handleTimeout,
  })

  // Restart the clock at the start of every turn. Keyed on turnId, not the
  // question key: a re-served question keeps its key, and without this the
  // timer stayed in its EXPIRED state so nothing on screen responded.
  // Spec table 5: "Timer starts fresh for new question."
  const prevTurnIdRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (turnId !== prevTurnIdRef.current) {
      prevTurnIdRef.current = turnId
      timer.reset()
    }
  }, [turnId, timer])

  // Pause the question timer once an answer is picked - ONLY when the
  // follow-ups have a clock of their own. With one shared window the timer must
  // keep running, because the 60 seconds is for the whole question: the answer
  // and the why/confidence together.
  React.useEffect(() => {
    if (BMD_FOLLOW_UPS_SHARE_QUESTION_TIMER) return
    if (selectedAnswerId != null && !timer.isPaused && !timer.isExpired) {
      timer.pause()
    }
  }, [selectedAnswerId, timer])


  // BOTH FOLLOW-UPS ARE OPTIONAL. They used to block submit, which made a
  // question unanswerable until the student had typed a reason and picked a
  // confidence - and for Reception to Year 3, who no longer see either field,
  // would have made submit permanently impossible.
  const canSubmit = selectedAnswerId != null && !submitting

  // The follow-up countdown fires from inside a timer callback, which closes
  // over whatever was current when it was armed. These refs let it read the
  // live values instead.
  React.useEffect(() => {
    selectedAnswerIdRef.current = selectedAnswerId
  }, [selectedAnswerId])

  const followUpRef = React.useRef(followUp)
  React.useEffect(() => {
    followUpRef.current = followUp
  }, [followUp])
  const canSubmitRef = React.useRef(canSubmit)
  React.useEffect(() => {
    canSubmitRef.current = canSubmit
  }, [canSubmit])

  // `allowPartialFollowUp` is only set by the follow-up timeout path: it
  // submits the answer the student already chose and lets the reason /
  // confidence go in as NULL rather than stranding them on a form they ran
  // out of time to finish.
  const submitAnswer = React.useCallback(
    async (allowPartialFollowUp = false) => {
      if (!currentQuestion || selectedAnswerId == null) return
      if (!allowPartialFollowUp && !canSubmitRef.current) return
      if (submittedForQuestionRef.current === currentQuestionKey) return
      submittedForQuestionRef.current = currentQuestionKey
      const topicKeyAtSubmit = currentTopicKey
      const followUpNow = followUpRef.current
      const reason = followUpNow.reason.trim()
      setSubmitError(null)
      setNotice(null)
      setSubmitting(true)
      try {
        const res = await submitBmdAnswer({
          diagnostics_session_key: diagnosticsSessionKey,
          pk_session_question_key: currentQuestion.pk_session_question_key,
          diagnostic_id: currentQuestion.fk_diagnostic_id,
          diagnostic_answer_id: selectedAnswerId,
          answer_reason: reason.length > 0 ? reason : null,
          answer_confident: followUpNow.confident,
          duration_seconds: computeDurationSeconds(),
        })
        await finishTurn(res, topicKeyAtSubmit)
      } catch (err) {
        setSubmitError(
          err instanceof Error ? err.message : BMD_COPY.ERROR_SUBMIT,
        )
        submittedForQuestionRef.current = null
      } finally {
        setSubmitting(false)
      }
    },
    [
      currentQuestion,
      currentQuestionKey,
      currentTopicKey,
      diagnosticsSessionKey,
      finishTurn,
      selectedAnswerId,
    ],
  )

  // Published for handleTimeout, which is defined above this point.
  React.useEffect(() => {
    submitAnswerRef.current = submitAnswer
  }, [submitAnswer])

  // The follow-up questions get their own countdown. It runs only while they
  // are on screen, after an answer is picked, because that is exactly when the
  // MCQ timer is paused.
  const handleFollowUpTimeout = React.useCallback(() => {
    if (BMD_FOLLOW_UP_TIMEOUT_ACTION === "SKIP") {
      handleTimeout()
      return
    }
    void submitAnswer(true)
  }, [handleTimeout, submitAnswer])

  const followUpTimer = useBmdTimer({
    mainSeconds: BMD_FOLLOW_UP_TIMER_SECONDS,
    // No grace period: the student has already answered the question itself,
    // this window is only for the two follow-ups.
    bufferSeconds: 0,
    autoStart: false,
    onTimeout: handleFollowUpTimeout,
  })

  // The follow-ups appear for EVERY year once an answer is picked - the
  // confidence rating is asked of all of them. Only the written reason waits
  // until Year 4, and the server decides that, so the boundary lives in one
  // place and a new year level inherits it. Undefined means an older backend:
  // fall back to asking, which is what every session did before.
  const writtenReasonAllowed = session?.written_reason_enabled !== false
  const followUpVisible = selectedAnswerId != null
  // Re-arm per turn, then start it the moment the follow-ups appear.
  React.useEffect(() => {
    followUpTimer.reset()
  }, [turnId])
  React.useEffect(() => {
    if (BMD_FOLLOW_UPS_SHARE_QUESTION_TIMER) return
    if (followUpVisible) followUpTimer.resume()
  }, [followUpVisible, followUpTimer])

  const handleSubmit = () => {
    void submitAnswer(false)
  }

  const handleManualSkip = async () => {
    if (!currentQuestion || submitting) return
    if (submittedForQuestionRef.current === currentQuestionKey) return
    submittedForQuestionRef.current = currentQuestionKey
    const topicKeyAtSubmit = currentTopicKey
    setSubmitError(null)
    setNotice(null)
    setSubmitting(true)
    try {
      const res = await skipBmdQuestion({
        diagnostics_session_key: diagnosticsSessionKey,
        pk_session_question_key: currentQuestion.pk_session_question_key,
        skip_reason: "MANUAL",
        duration_seconds: computeDurationSeconds(),
      })
      await finishTurn(res, topicKeyAtSubmit)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : BMD_COPY.ERROR_SUBMIT)
      submittedForQuestionRef.current = null
    } finally {
      setSubmitting(false)
    }
  }

  // Session-complete detection: loaded, no error, no current question.
  React.useEffect(() => {
    if (!loading && !error && session && !session.current_question) {
      onSessionComplete?.()
    }
  }, [loading, error, session, onSessionComplete])

  if (loading && !session) {
    return (
      <div className={className}>
        <p className="text-muted mb-0">{BMD_COPY.LOADING}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={className}>
        <p className="text-danger mb-0">{error}</p>
      </div>
    )
  }

  if (!session) {
    return null
  }

  if (!currentQuestion) {
    // Nothing left to serve. That isn't the same as "finished": a topic sitting
    // at COMPLETED (rather than REMEDIATED) still owes remediation that hasn't
    // been generated yet, so the student shouldn't be congratulated on being
    // done. Mirrors the same distinction on the welcome/resume screen.
    const remediationPending = session.topics.some(
      (t) => t.topic_status === "COMPLETED",
    )
    // Same three-way choice as before, now shown on the mockup's card. Which
    // message appears is unchanged; only where it sits.
    const body = remediationPending
      ? BMD_COPY.SESSION_PAUSED_FOR_REMEDIATION
      : session.session_kind === "PREREQ"
      ? BMD_COPY.PREREQ_SESSION_COMPLETE
      : BMD_COPY.SESSION_COMPLETE
    const answered =
      session.session_kind === "PREREQ"
        ? session.answered_prereq_questions
        : session.answered_base_questions
    return (
      <div className={className}>
        <BmdFinishCard
          sectionCount={session.topics.length}
          questionsAnswered={answered}
          body={body}
          onBack={onBack}
        />
      </div>
    )
  }

  // Progress: locate the current topic in the list so we can render "n of m"
  // counts. Fallbacks keep the UI coherent if the server hasn't filled topic
  // metadata yet.
  const currentTopic = session.topics.find(
    (t) => t.pk_session_topic_key === session.current_topic_key,
  )

  // FM_RETRY questions are counted separately from the base pass. They are
  // appended after the base questions, so folding them into one total produced
  // "(11/10)", a counter past its own denominator, and made the test look like
  // it grew because the student answered badly. Spec 5.1 frames these as a
  // second attempt, so they get their own counter.
  //
  // Plain const, not a hook: this sits after the early returns above, where
  // calling a hook would break the rules-of-hooks ordering.
  const isPrereqQuestion = String(currentQuestion?.phase || "").startsWith(
    "PREREQ_L",
  )
  const progress =
    isPrereqQuestion && currentTopic
      ? {
          // The prerequisite test is its own activity, so there is no base pass
          // to count against: the block's own questions are the whole of it.
          isRetry: false,
          current: Math.min(
            currentTopic.prereq_answered_count + 1,
            Math.max(currentTopic.prereq_question_count, 1),
          ),
          total: Math.max(currentTopic.prereq_question_count, 1),
        }
      : currentQuestion?.phase === "FM_RETRY" && currentTopic
      ? {
          isRetry: true,
          current: Math.min(
            currentTopic.retry_answered_count + 1,
            Math.max(currentTopic.retry_question_count, 1),
          ),
          total: Math.max(currentTopic.retry_question_count, 1),
        }
      : {
          isRetry: false,
          current: Math.min(
            (currentTopic?.answered_count ?? 0) + 1,
            Math.max(currentTopic?.base_question_count ?? 1, 1),
          ),
          total: Math.max(currentTopic?.base_question_count ?? 1, 1),
        }

  return (
    <div className={className}>
      {/* Header: topic name + progress + timer. The BMD base year level is
          deliberately NOT rendered here — it's tutor-only context per the
          boss's directive. */}
      <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {currentTopic ? (
            <>
              <div className="mb-2" style={{ fontSize: 14 }}>
                <span className="fw-bold" style={{ color: BMD_UI.FILL }}>
                  {plainText(currentTopic.topic_name)}
                </span>
                <span style={{ color: BMD_UI.MUTED }} className="mx-2">
                  ·
                </span>
                <span style={{ color: BMD_UI.BODY }}>
                  Question {progress.current} of {progress.total}
                </span>
                {progress.isRetry ? (
                  <span style={{ color: BMD_UI.MUTED }} className="ms-2">
                    · Second attempt
                  </span>
                ) : null}
              </div>
              <BmdQuestionTracker
                current={progress.current}
                total={progress.total}
              />
            </>
          ) : null}
        </div>
        <BmdTimer
          className="flex-shrink-0"
          remainingSeconds={timer.remainingSeconds}
          phase={timer.phase}
          isPaused={timer.isPaused}
        />
      </div>

      {/* Question + answer options. Keyed by session_question_key so the
          whole DQDisplay subtree (including its MathJax wrapper) fully
          unmounts and remounts between questions — defensive belt-and-braces
          on top of useDQ's null-reset + MathJax's own key. */}
      <DQDisplay
        // Keyed on the turn, not the question. A skipped question returns at
        // the end of the topic with the same key, so keying on the key alone
        // meant DQDisplay never remounted and kept its internal selection: the
        // student saw their earlier pick still highlighted while the player had
        // cleared it, and the two disagreed about what was chosen.
        // Remounting refetches the DQ, which is safe: the option order is a
        // deterministic shuffle seeded by pk_session_question_key, so a
        // re-served question comes back in exactly the same order.
        key={turnId}
        dqId={currentQuestion.fk_diagnostic_id}
        mode="test"
        sessionQuestionKey={currentQuestion.pk_session_question_key}
        onAnswerSelected={(answerId) => setSelectedAnswerId(answerId)}
        // Freeze the options the moment an answer is picked. That mattered
        // most when picking an answer PAUSED the clock: a student could choose
        // anything to stop it, think as long as they liked, then switch. With a
        // single shared window the clock keeps running, so the incentive is
        // gone - but the lock still stops a reason describing one answer being
        // submitted against another.
        locked={BMD_LOCK_ANSWER_AFTER_SELECT && selectedAnswerId != null}
        className="mb-4"
      />

      {/* Follow-ups appear once an answer is picked.

          Their own countdown is shown ONLY when they have one. With a single
          shared window the question timer is still running and is the only
          clock that means anything - a second number counting down beside it
          would be telling the student something untrue.

          Every year sees this block; only the written reason inside it is
          gated, by showReason. */}
      {followUpVisible ? (
        <>
          {!BMD_FOLLOW_UPS_SHARE_QUESTION_TIMER ? (
          <div className="d-flex justify-content-end align-items-center mb-1">
            <span
              className={`fw-bold font-size-14 ${
                followUpTimer.remainingSeconds <= 10
                  ? "text-danger"
                  : "text-muted"
              }`}
            >
              {followUpTimer.remainingSeconds}s
            </span>
          </div>
          ) : null}
          <FollowUpQuestions
            value={followUp}
            onChange={setFollowUp}
            showReason={writtenReasonAllowed}
            className="mb-3"
          />
        </>
      ) : null}

      {/* Action row, above a hairline so it reads as the end of the question
          rather than another option. Skip carries its subtext because "Skip for
          now" alone reads as "give up on this one", and a child who believes
          that will guess instead of skipping - which is the one thing the skip
          exists to prevent. */}
      <hr
        style={{ borderColor: BMD_UI.OPTION_BORDER, margin: "24px 0 16px" }}
      />
      <div className="d-flex justify-content-between align-items-center gap-3">
        <button
          type="button"
          className="btn btn-link p-0 text-start text-decoration-none"
          onClick={handleManualSkip}
          disabled={submitting}
        >
          <span
            className="d-block fw-medium"
            style={{ color: BMD_UI.TITLE, fontSize: 14 }}
          >
            {BMD_COPY.SKIP_FOR_NOW}
          </span>
          <span style={{ color: BMD_UI.MUTED, fontSize: 12 }}>
            {BMD_COPY.SKIP_SUBTEXT}
          </span>
        </button>
        <button
          type="button"
          className="btn fw-bold text-white d-flex align-items-center gap-2 flex-shrink-0"
          style={{
            backgroundColor: BMD_UI.ACCENT,
            borderColor: BMD_UI.ACCENT,
            borderRadius: 8,
            padding: "12px 24px",
            opacity: canSubmit ? 1 : 0.5,
          }}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? BMD_COPY.LOADING : BMD_COPY.SUBMIT_ANSWER_CTA}
          <i className="fas fa-arrow-right" aria-hidden="true"></i>
        </button>
      </div>

      {submitError ? (
        <p className="text-danger mt-3 mb-0">{submitError}</p>
      ) : null}

      {/* Non-blocking notice. Replaces the modal for a failed fundamental-
          misconception retry: the student is informed but keeps moving. */}
      {notice ? (
        <div
          className="d-flex align-items-center gap-2 mt-3 p-2 rounded"
          style={{ backgroundColor: "#FFF6E5", color: "#8A5A00" }}
          role="status"
        >
          <i className="fas fa-user-shield" aria-hidden="true"></i>
          <span className="font-size-13">{notice}</span>
        </div>
      ) : null}

      {/* Intervention modal. Blocks the flow until the student acknowledges,
          so the "tutor has been notified" message cannot be missed. Advancing
          is deferred via pendingAdvance so the refetch happens after the
          click. */}
      <Modal
        isOpen={interventionMessage != null}
        backdrop="static"
        keyboard={false}
        centered
      >
        <ModalBody className="text-center py-4">
          <i className="fas fa-user-shield font-size-48 text-warning mb-3"></i>
          <h4 className="fw-bold mb-2">Tutor notified</h4>
          <p className="mb-0">{interventionMessage}</p>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <button
            type="button"
            className="btn btn-danger cs-btn-danger text-uppercase"
            onClick={advanceFromIntervention}
          >
            Continue
          </button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default BmdTestPlayer
